import {
  type ChangeSpec,
  type EditorState,
  EditorSelection,
  Facet,
  type TransactionSpec,
} from "@codemirror/state";
import type { EditorView, KeyBinding } from "@codemirror/view";

/**
 * Pure markdown editing commands over `EditorState`. Each `*Spec` function returns a
 * `TransactionSpec` (or `null` when there is nothing to do) so it can be unit tested with
 * `state.update(spec)` and no DOM. The `Command`-shaped wrappers below dispatch them.
 */

export const BOLD_MARKER = "**";
/** `_` rather than `*` so italic never has to disambiguate against a surrounding `**`. */
export const ITALIC_MARKER = "_";
export const CODE_MARKER = "`";

type CommandTarget = { state: EditorState; dispatch: (tr: TransactionSpec) => void };

/**
 * Wraps the selection (or the word at the cursor) in `marker`, or unwraps it when it is already
 * wrapped, either inside the selection (`**bold**` selected) or around it (`bold` selected inside
 * `**bold**`). With no selection and no word, inserts a marker pair and puts the cursor between.
 */
export function toggleWrapSpec(state: EditorState, marker: string): TransactionSpec {
  const m = marker.length;
  return state.changeByRange((range) => {
    let { from, to } = range;
    if (from === to) {
      const word = state.wordAt(from);
      if (word) {
        from = word.from;
        to = word.to;
      } else {
        return {
          changes: { from, insert: marker + marker },
          range: EditorSelection.cursor(from + m),
        };
      }
    }
    const text = state.sliceDoc(from, to);
    if (text.length >= 2 * m && text.startsWith(marker) && text.endsWith(marker)) {
      return {
        changes: { from, to, insert: text.slice(m, text.length - m) },
        range: EditorSelection.range(from, to - 2 * m),
      };
    }
    if (
      from - m >= 0 &&
      state.sliceDoc(from - m, from) === marker &&
      state.sliceDoc(to, to + m) === marker
    ) {
      return {
        changes: [
          { from: from - m, to: from },
          { from: to, to: to + m },
        ],
        range: EditorSelection.range(from - m, to - m),
      };
    }
    return {
      changes: [
        { from, insert: marker },
        { from: to, insert: marker },
      ],
      range: EditorSelection.range(from + m, to + m),
    };
  });
}

export const toggleBoldSpec = (state: EditorState) => toggleWrapSpec(state, BOLD_MARKER);
export const toggleItalicSpec = (state: EditorState) => toggleWrapSpec(state, ITALIC_MARKER);
export const toggleInlineCodeSpec = (state: EditorState) => toggleWrapSpec(state, CODE_MARKER);

const TASK_LINE = /^(\s*)(?:([-*+]|\d+[.)])\s+)?(?:\[([ xX])\](\s?))?/;

/**
 * Toggles a GFM task checkbox on every line the selection touches:
 * `- [ ] a` ⇄ `- [x] a`; `- a` and `a` become `- [ ] a`; an empty line becomes `- [ ] `.
 * Lines that already have a box follow the first boxed line (unchecked there checks them all);
 * lines without one always gain `[ ]`. Only the box or the missing prefix is edited so the
 * selection maps through unchanged.
 */
export function toggleTaskCheckboxSpec(state: EditorState): TransactionSpec | null {
  const lines = new Map<number, { from: number; text: string }>();
  for (const range of state.selection.ranges) {
    const first = state.doc.lineAt(range.from).number;
    const last = state.doc.lineAt(range.to).number;
    for (let n = first; n <= last; n++) {
      const line = state.doc.line(n);
      lines.set(n, { from: line.from, text: line.text });
    }
  }
  let checkAll: boolean | undefined;
  const changes: ChangeSpec[] = [];
  for (const line of [...lines.values()].sort((a, b) => a.from - b.from)) {
    const match = TASK_LINE.exec(line.text);
    if (!match) continue;
    const indent = match[1] ?? "";
    const bullet = match[2];
    const box = match[3];
    if (box === undefined) {
      // No checkbox yet: add an unchecked one after the bullet, or a bullet and box.
      const at = bullet === undefined ? line.from + indent.length : line.from + match[0].length;
      changes.push({ from: at, insert: bullet === undefined ? "- [ ] " : "[ ] " });
      continue;
    }
    const checked = box !== " ";
    checkAll ??= !checked;
    if (checked === checkAll) continue;
    const boxPos = line.from + match[0].length - (match[4]?.length ?? 0) - 2;
    changes.push({ from: boxPos, to: boxPos + 1, insert: checkAll ? "x" : " " });
  }
  if (changes.length === 0) return null;
  return { changes };
}

const URL_LIKE = /^(https?:\/\/|mailto:)\S+$/i;

/**
 * Inserts a markdown link. A selected URL becomes `[](url)` with the cursor in the brackets;
 * selected text (or the word at the cursor) becomes `[text](url)` with `url` selected so the
 * next keystrokes replace it; nothing selected inserts `[text](url)` with `text` selected.
 */
export function insertLinkSpec(state: EditorState): TransactionSpec {
  return state.changeByRange((range) => {
    let { from, to } = range;
    if (from === to) {
      const word = state.wordAt(from);
      if (word) {
        from = word.from;
        to = word.to;
      }
    }
    const text = state.sliceDoc(from, to);
    if (URL_LIKE.test(text)) {
      return {
        changes: { from, to, insert: `[](${text})` },
        range: EditorSelection.cursor(from + 1),
      };
    }
    const label = text.length > 0 ? text : "text";
    const insert = `[${label}](url)`;
    const urlStart = from + label.length + 3;
    return {
      changes: { from, to, insert },
      range:
        text.length > 0
          ? EditorSelection.range(urlStart, urlStart + 3)
          : EditorSelection.range(from + 1, from + 1 + label.length),
    };
  });
}

function run(spec: (state: EditorState) => TransactionSpec | null) {
  return (target: CommandTarget): boolean => {
    const tr = spec(target.state);
    if (!tr) return false;
    target.dispatch({ ...tr, userEvent: "input", scrollIntoView: true });
    return true;
  };
}

export const toggleBold = run(toggleBoldSpec);
export const toggleItalic = run(toggleItalicSpec);
export const toggleInlineCode = run(toggleInlineCodeSpec);
export const toggleTaskCheckbox = run(toggleTaskCheckboxSpec);
export const insertLink = run(insertLinkSpec);

/** One leading list, checkbox or quote marker. Applied repeatedly so `> - [ ] x` unwraps fully. */
const LINE_MARKER = /^(?:[-*+](?:\s+|$)|\d+[.)](?:\s+|$)|\[[ xX]\]\s*|>\s*|#{1,6}(?:\s+|$))/;

/**
 * The todo title for a line or selection: leading whitespace, list bullets (`- `, `* `, `+ `),
 * numbering (`1. `), task boxes (`[ ]`, `[x]`), blockquote markers and heading hashes are
 * stripped from every line, and the non-empty lines are joined with single spaces.
 */
export function lineToTodoTitle(text: string): string {
  const parts: string[] = [];
  for (const raw of text.split("\n")) {
    let line = raw.trim();
    let stripped = line.replace(LINE_MARKER, "");
    while (stripped !== line) {
      line = stripped.trim();
      stripped = line.replace(LINE_MARKER, "");
    }
    if (line) parts.push(line);
  }
  return parts.join(" ");
}

/** What the editor hands to React when the user asks to turn a line into a todo. */
export type AddTodoRequest = {
  /** The line under the cursor (the first selected line when text is selected). */
  lineText: string;
  /** The selected text; empty when the selection is a cursor. */
  selectionText: string;
  /** `lineToTodoTitle` of the selection when there is one, else of the line. */
  title: string;
  /** Viewport coordinates of the cursor, `null` when the view cannot measure (not laid out). */
  coords: { left: number; top: number; bottom: number } | null;
};

/** Pure part of `addTodo`: the request without coordinates. */
export function addTodoRequestOf(state: EditorState): Omit<AddTodoRequest, "coords"> {
  const main = state.selection.main;
  const lineText = state.doc.lineAt(main.from).text;
  const selectionText = state.sliceDoc(main.from, main.to);
  const title = lineToTodoTitle(selectionText.trim() ? selectionText : lineText);
  return { lineText, selectionText, title };
}

/**
 * Receives `AddTodoRequest`s. `NoteEditor` provides one handler that forwards to React; with no
 * handler configured the command reports itself as not applicable so the key falls through.
 */
export const addTodoHandler = Facet.define<(request: AddTodoRequest) => void>();

/** Asks the host to open the "Add as todo" popover for the selection or line. */
export const addTodo = (view: EditorView): boolean => {
  const handlers = view.state.facet(addTodoHandler);
  if (handlers.length === 0) return false;
  const request = addTodoRequestOf(view.state);
  const at = view.coordsAtPos(view.state.selection.main.head);
  const coords = at ? { left: at.left, top: at.top, bottom: at.bottom } : null;
  for (const handler of handlers) handler({ ...request, coords });
  return true;
};

/**
 * The editor's own bindings. `Mod-Enter` shadows the default `insertBlankLine`; nothing else
 * collides with `defaultKeymap` or `markdownKeymap`. Tab is deliberately not bound
 * (`indentWithTab` is off) so keyboard users can tab out of the editor.
 */
export const editorKeymap: readonly KeyBinding[] = [
  { key: "Mod-b", run: toggleBold, preventDefault: true },
  { key: "Mod-i", run: toggleItalic, preventDefault: true },
  { key: "Mod-e", run: toggleInlineCode, preventDefault: true },
  { key: "Mod-k", run: insertLink, preventDefault: true },
  { key: "Mod-Enter", run: toggleTaskCheckbox, preventDefault: true },
];

/** Prefix/suffix-aware replacement of the whole document so the selection maps through it. */
export function replaceDocSpec(state: EditorState, next: string): TransactionSpec | null {
  const current = state.doc.toString();
  if (current === next) return null;
  let prefix = 0;
  const max = Math.min(current.length, next.length);
  while (prefix < max && current.charCodeAt(prefix) === next.charCodeAt(prefix)) prefix++;
  let suffix = 0;
  while (
    suffix < max - prefix &&
    current.charCodeAt(current.length - 1 - suffix) === next.charCodeAt(next.length - 1 - suffix)
  )
    suffix++;
  return {
    changes: {
      from: prefix,
      to: current.length - suffix,
      insert: next.slice(prefix, next.length - suffix),
    },
  };
}
